import { Alert, AlertColor, AlertProps, IconButton, Typography } from '@mui/material';
import { forwardRef } from 'react';
import { CustomContentProps, SnackbarContent, SnackbarMessage, closeSnackbar } from 'notistack';
import {
  CloseIcon,
  CrossCircleIcon,
  WarningIcon,
  InfoIcon,
  CheckmarkCircleIcon
} from '../../../../icons';

interface FlagBaseProps {
  variant: CustomContentProps['variant'];
  message: SnackbarMessage;
  details?: string;
  action: AlertProps['action'];
  onClose?: () => void;
  moveActionButton?: boolean;
}

const FlagBase = forwardRef<HTMLDivElement, FlagBaseProps>(
  ({ message, details, variant = 'default', onClose, action, moveActionButton }, ref) => {
    const iconMapping = {
      default: <></>,
      error: <CrossCircleIcon color="error.main" />,
      warning: <WarningIcon color="warning.main" />,
      info: <InfoIcon color="info.main" />,
      success: <CheckmarkCircleIcon color="success.main" />
    };

    const variantMapping: Record<typeof variant, AlertColor | undefined> = {
      default: undefined,
      info: 'info',
      error: 'error',
      success: 'success',
      warning: 'warning',
      detailed: undefined,
      detailedInfo: 'info',
      detailedSuccess: 'success',
      detailedWarning: 'warning',
      detailedError: 'error'
    };

    const combinedAction =
      action && onClose ? (
        <>
          {action}
          <IconButton sx={{ padding: '5px', marginLeft: '0px' }} onClick={onClose}>
            <CloseIcon color="common.white" />
          </IconButton>
        </>
      ) : (
        action
      );

    return (
      <Alert
        ref={ref}
        severity={variantMapping[variant]}
        className="shadow-700"
        sx={{
          maxWidth: '350px',
          minWidth: '200px',
          backgroundColor: `alert.${variant}.light`,
          color: `alert.${variant}.main`,
          boxShadow: 'inherit',
          paddingY: '8px',
          borderRadius: '12px',
          '& .MuiAlert-icon': {
            marginRight: variant !== 'default' ? '12px' : '0px',
            paddingY: '6px'
          },
          '& .MuiAlert-action': {
            paddingTop: 0,
            paddingRight: onClose || moveActionButton ? '0' : '8px',
            paddingLeft: onClose && !moveActionButton ? '16px' : '8px'
          },
          '& button': {
            color: 'grey.100',
            fontSize: '1rem'
          },
          '& .MuiAlert-message': {
            display: moveActionButton ? 'grid' : 'block',
            verticalAlign: 'sub',
            gridTemplateColumns: '1fr auto',
            '& > *:last-child': {
              gridColumn: '1 / span 2',
              justifySelf: 'end',
              paddingBottom: '0px'
            }
          }
        }}
        icon={iconMapping[variantMapping[variant] ?? 'default']}
        action={!moveActionButton && combinedAction}
        slots={{ closeIcon: CloseIcon }}
        slotProps={{ closeIcon: { color: 'inherit' } }}
        onClose={onClose}
      >
        <Typography variant="body2" className={`font-medium text-alert-${variant}-main `}>
          {message}
        </Typography>
        {Boolean(details) && <Typography variant="caption">{details}</Typography>}
        {onClose && moveActionButton && (
          <IconButton
            sx={{
              padding: '0px',
              marginLeft: '0px',
              marginY: 'auto',
              display: 'inline',
              alignSelf: 'flex-start'
            }}
            onClick={onClose}
            color="primary"
          >
            <CloseIcon />
          </IconButton>
        )}
        {moveActionButton && action}
      </Alert>
    );
  }
);

export interface FlagProps extends CustomContentProps {
  isCloseable: boolean;
  moveActionButton: boolean;
  details?: string;
}

const fetchFlagVariant = (variant: CustomContentProps['variant']) =>
  forwardRef<HTMLDivElement, FlagProps>(({ id, message, action, ...other }, ref) => {
    return (
      <SnackbarContent ref={ref} role="alert" key={id} {...other}>
        <FlagBase
          variant={variant}
          message={message}
          onClose={() => closeSnackbar(id)}
          action={(typeof action === 'function' ? action(id) : action) ?? undefined}
          moveActionButton={other.moveActionButton}
          details={other.details}
        />
      </SnackbarContent>
    );
  });

export const DefaultFlag = fetchFlagVariant('default');

export const ErrorFlag = fetchFlagVariant('error');

export const WarningFlag = fetchFlagVariant('warning');

export const InfoFlag = fetchFlagVariant('info');

export const SuccessFlag = fetchFlagVariant('success');

export default FlagBase;
